import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import config from 'config';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import ProductGridRelatedProductsContext from '~/grid/context/ProductGridRelatedProductsContext';
import useInitialProductPricing from '~/grid/utils/useInitialProductPricing';
import useInitialProductRelatedProducts from '~/grid/utils/useInitialProductRelatedProducts';
import { useReportAlgorithmTrace } from '~/grid/utils/useReportAlgorithmTrace';
import CloseIcon from '~/icons/components/CloseIcon';
import CarouselNavigation from '~/shared/components/CarouselNavigation';
import LoadingSpinnerIcon from '~/shared/components/LoadingSpinner';
import TypedText from '~/shared/components/TypedText';
import featureNames from '~/shared/constants/featureFlags';
import {
  overlayColor,
  imageAspectRatio,
  pageSize,
} from '~/shared/constants/productGrid';
import { cookieSizeMap } from '~/shared/constants/productSizes';
import { useDefaultProductFilters } from '~/shared/useDefaultProductFilters';
import { getFilterSettings } from '~/shared/utils/getFilterSettings';
import { modifyFiltersForLastChance } from '~/shared/utils/modifyFiltersForLastChance';
import { useCioClient } from '~/shared/utils/useCioClient';
import useGender from '~/shared/utils/useGender';
import useHasFeature from '~/shared/utils/useHasFeature';
import { useCategoryLabels, useOptionLabels } from '~/shared/utils/useLabels';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { useAdminTools } from '~/techstyle-shared/react-admin';
import { useAssets } from '~/techstyle-shared/react-assets';
import {
  CategoryLabelProvider,
  EmptyGridMessage,
  Button,
  FilterDrawerCustomFilters as BaseFilterDrawer,
  FilterDrawerButton as BaseFilterDrawerButton,
  FloatingFilterButton as BaseFloatingFilterButton,
  GridProvider,
  HeaderAttachment as BaseHeaderAttachment,
  LoadingStatus,
  OptionLabelProvider,
  ProductBrowserCustomFilters as BaseProductBrowser,
  ProductBrowserSideBar,
  ProductListingFilterStyle,
  ProductSizeFilter as BaseProductSizeFilter,
  TopLevelCategoryHeading as BaseCategoryHeader,
  mobile,
  desktop,
  useBreakpoint,
  useGridState,
  useLoadingStatus,
  useProductBrowser,
  slugify,
  ProductGridPricingContext,
} from '~/techstyle-shared/react-components';
import { useApolloClient } from '~/techstyle-shared/react-graphql';
import { FormattedMessage, useRegion } from '~/techstyle-shared/react-intl';
import {
  ProductListViewedProvider,
  useProductActions,
  useProductListingsReducer,
} from '~/techstyle-shared/react-products';
import { VirtualScroll } from '~/techstyle-shared/react-virtual-scroll';
import { useCookies, useDomain } from '~/techstyle-shared/redux-core';

import { BreakpointsShape } from '../../constants/customPropTypes';
import FloatingFilterBarContext from '../../context/FloatingFilterBarContext';
import { useGridScrollContext } from '../../context/GridScrollContext';
import addNewGridColumnSettings, {
  gridColumnsSettingsType,
} from '../../utils/addNewGridColumnSettings';
import getSizeCookies from '../../utils/getSizeCookies';
import { getColorSwatches } from '../../utils/productFilters.js';
import useProductImageType from '../../utils/useProductImageType';
import CategoryDropdown from '../CategoryDropdown';
import DesktopProductListingFilter from '../DesktopProductListingFilter';
import FilterTags from '../FilterTags';
import FloatingFilterBar from '../FloatingFilterBar/FloatingFilterBar';
import GridFilterSegmentEvents from '../GridFilterSegmentEvents/GridFilterSegmentEvents';
import MobileProductListingFilter from '../MobileProductListingFilter';
import { useProductBrowserFilterState } from '../ProductBrowserFilterContext';
import ProductCategoryTree from '../ProductCategoryTree';
import BaseProductGrid from '../ProductGrid';
import BaseSortDropdown from '../SortDropdown';

import {
  filterFutureProducts,
  getCioBrowseProducts,
  getCioSearchProducts,
  getGqlProducts,
  getV2Products,
} from './utils';

const DynamicFormattedMessage = FormattedMessage;

const LoadingStatusContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  /* Left position will need to change if width of filters sidebar change
 in order to keep it centered on desktop */
  left: calc(50% + 150px);
  transform: translateX(-50%);
  right: 0;
  z-index: 3;

  ${mobile`
		left: 50%;
  `}
`;

const LoadingIconWrapper = styled.div`
  position: sticky;
  line-height: 0.7;
  top: 50%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 44px;
`;

const ResultsCountWrapper = styled(TypedText).attrs({ size: 16 })`
  padding-top: 16px;
  min-height: 36px;
`;

const GridContent = styled.div`
  flex: 1 1 auto;
`;

const MobileFiltersOnlySortPlaceholder = styled.div``;

const Breadcrumbs = styled(BaseProductBrowser.CategoryBreadcrumbs)`
  font-size: 14px;
  flex: 0 0 268px;
  margin-right: 32px;
`;

const breadcrumbItemStyle = css`
  color: ${({ isActive, theme }) =>
    isActive ? theme.colors.activeBreadcrumb : theme.colors.inactiveItem};
  font-weight: ${({ isActive }) => (isActive ? `600` : `400`)};
`;

const FloatingFilterButton = styled(BaseFloatingFilterButton)`
  border-radius: 23px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.white};
  height: 34px;
  min-width: 88px;
  z-index: 0;
  position: absolute;
  bottom: 0;
  transform: translateY(50px);

  ${mobile`
    order: 2;
  `}

  ${({ theme }) => theme.floatingFilterButton};
`;

const filterDrawerDialogStyle = css`
  box-shadow: -4px 0 8px 0 rgba(0, 0, 0, 0.04),
    inset 1px 0 0 0 ${({ theme }) => theme.colors.palette.white};
`;

const noFiltersAppliedStyle = css`
  font-size: 16px;
  letter-spacing: normal;
  margin-bottom: 16px;

  ${({ theme }) => theme.gridFilterStyles.noFiltersAppliedStyle}
`;

const applyFiltersButtonStyle = css`
  &&& {
    letter-spacing: normal;
    color: ${({ theme }) => theme.colors.textLight};
    opacity: ${({ disabled }) => (disabled ? 0.25 : 1)};

    ${({ theme }) => theme.gridFilterStyles.applyFiltersButtonStyle}
  }
`;

const filterTagWrapperStyle = css`
  position: unset;
  padding: 16px 24px;
`;

const FilterDrawerButton = styled(BaseFilterDrawerButton)`
  font-size: 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.fontStacks.textFamily};
  color: ${({ theme }) => theme.colors.textDefault};

  ${({ theme }) => theme.gridStyles.filterDrawerButton};
`;

const HeaderAttachment = styled(BaseHeaderAttachment)``;

const filterCountStyle = css`
  color: ${({ theme }) => theme.colors.textDefault};
`;

const TopLevelCategoryHeading = styled(BaseCategoryHeader)`
  ${({ theme }) => theme.gridStyles.topLevelCategoryHeading}
`;

const closeButtonStyle = css`
  position: unset;
  display: flex;
  margin-left: auto;
  color: ${({ theme }) => theme.colors.closeButton};
  width: unset;
  padding: 24px 24px 0 0;
  flex-grow: 0;

  span {
    svg {
      width: 15px;
      height: 15px;
    }
    span {
      display: none;
    }
  }
`;

const BrowserHeader = styled(BaseProductBrowser.Header)`
  ${desktop`
    position: relative;
    display: flex;
    padding: 20px 32px 0 32px;
    margin-top: 32px;
    margin-bottom: 24px;
    justify-content: space-between;
    align-items: center;
  `}
`;

const GridHeaderWrapper = styled.div`
  width: 100%;
`;

const GridHeader = styled.h1`
  ${({ theme }) => theme.gridHeaderStyle};
`;

const SortDropdown = styled(BaseSortDropdown)`
  ${mobile`
    margin-right: auto;
  `}
`;

const Overlay = styled.div`
  ${mobile`
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    background-color: ${overlayColor};
    z-index: 1;
  `}
`;

const NoProductsMessage = styled(EmptyGridMessage)`
  display: flex;
  justify-content: center;
  padding: 186px 0;
`;

const BreadcrumbAnchor = styled.a`
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const NoBreadcrumbsPlaceholder = styled.div`
  flex: 0 0 268px;
  margin-right: 32px;
  ${mobile`
    display: none;
  `}
`;

const CarouselHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const CarouselLabel = styled.label`
  ${({ theme }) => theme.gridStyles.gridCarouselLabelStyle}
`;

const CarouselNavigationContainer = styled.div`
  display: flex;
  align-items: center;
`;

const VerticalSpacer = styled.div`
  margin-right: ${({ theme: { sizes } }) => sizes.md}px;
  ${mobile`
    margin-right: 10px;
  `}
`;

const CarouselViewAllBtn = styled(Button)`
  ${({ theme }) => theme.gridStyles.gridCarouselViewAllCta}
`;

function LoadingGridsSpinner() {
  return (
    <LoadingStatus.LoadingIndicator>
      <LoadingStatusContainer data-autotag="prodgrid-loading-symbol">
        <LoadingIconWrapper>
          <LoadingSpinnerIcon />
          <DynamicFormattedMessage
            id="global_cta.loader_default_text"
            defaultMessage="Loading Products"
          />
        </LoadingIconWrapper>
      </LoadingStatusContainer>
    </LoadingStatus.LoadingIndicator>
  );
}

const EMPTY_ARRAY = [];

const showMobileInGridAssets = config.get(
  'public.brand.showMobileInGridAssets'
);

const DropdownOverlay = () => {
  const { overlayVisible } = useProductBrowser();

  return overlayVisible ? <Overlay /> : null;
};

export const individualSortOptionPropType = PropTypes.shape({
  value: PropTypes.string.isRequired,
  defaultMessage: PropTypes.string.isRequired,
  isDefault: PropTypes.bool,
  sort: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.shape({
        field: PropTypes.string,
        direction: PropTypes.oneOf(['asc', 'desc']),
      })
    ),
  ]),
});

const Header = ({
  shouldUseSharedFilters = false,
  disabledFilters = false,
  gridCTA = {},
  label,
  labelStyle,
  gender = null,
  isCarouselGrid = false,
  hasStyleGuide,
  hideSortOption = false,
  hideSideBar,
  isTabGrid,
  shouldShowCategoryNav = true,
  ...rest
}) => {
  const { isMobile } = useBreakpoint();
  const { breadcrumbs, sortOptions, filters } = useProductBrowser();
  const gridContext = useGridScrollContext();
  const desktopRef = gridContext?.desktopRef || null;
  const hasBreadcrumbs =
    (shouldShowCategoryNav && breadcrumbs?.length) || false;

  const hasSortDropdown = useMemo(() => {
    return (
      sortOptions &&
      sortOptions.length > 0 &&
      !shouldUseSharedFilters &&
      !disabledFilters
    );
  }, [sortOptions, shouldUseSharedFilters, disabledFilters]);

  const showSortDropdown = useMemo(() => {
    return hasSortDropdown && !hideSortOption && !isCarouselGrid;
  }, [hasSortDropdown, hideSortOption, isCarouselGrid]);

  const hasGridCTA = isMobile ? gridCTA.mobile : gridCTA.desktop;

  const showFilters =
    !disabledFilters &&
    !shouldUseSharedFilters &&
    filters?.aggregations?.length > 0;

  // We only want to show the header if we have: a header, a sort dropdown, filters, or a carousel Grid. Otherwise do not show the BrowserHeader container at all
  const shouldShowHeader =
    hasBreadcrumbs || // If we have breadcrumbs, then we need the header
    (!isMobile && !isCarouselGrid && label && typeof label === 'string') || // if we have a non-empty header label, then we need the header
    showSortDropdown || // If we have a sort dropdown, then we need the header
    (isMobile && showFilters) || // If we have a filters, on mobile they are shown in the header
    (isCarouselGrid && !isMobile); // check for carouselGrid (this will display the lef/right arrows for the carousel), then we need the header. However, if we are mobile, the title is handled in <GridNode />

  const shouldShowSidebar = !hideSideBar && !isCarouselGrid;

  // If we do not have anything to show in the header, return nothing
  if (!shouldShowHeader) {
    return null;
  }

  return (
    <BrowserHeader $isCarouselGrid={isCarouselGrid} ref={desktopRef} {...rest}>
      {shouldShowSidebar && hasBreadcrumbs && (
        <Breadcrumbs
          itemStyle={breadcrumbItemStyle}
          labels={breadcrumbs?.map((breadcrumb, index) => {
            const isActive = index === breadcrumbs.length - 1;
            return isActive ? (
              <DynamicFormattedMessage
                id={`site_product_category.${slugify(breadcrumb.label, '_')}`}
                defaultMessage={breadcrumb.label}
              />
            ) : (
              <BreadcrumbAnchor href={breadcrumb.urlPath}>
                <DynamicFormattedMessage
                  id={`site_product_category.${slugify(breadcrumb.label, '_')}`}
                  defaultMessage={breadcrumb.label}
                />
              </BreadcrumbAnchor>
            );
          })}
        />
      )}

      {/* We have filters (size, color, etc.) and a header label but no category nav breadcrumbs (e.g. womens/tops)
    then we need a buffer space for the header label */}
      {shouldShowSidebar && !hasBreadcrumbs && label && (
        <NoBreadcrumbsPlaceholder />
      )}

      {!isMobile && !isCarouselGrid && typeof label === 'string' && label && (
        <GridHeaderWrapper>
          <GridHeader>{label}</GridHeader>
        </GridHeaderWrapper>
      )}

      {showSortDropdown && <SortDropdown />}

      {showFilters && (
        <>
          {!showSortDropdown && <MobileFiltersOnlySortPlaceholder />}
          {hasStyleGuide ? (
            <FloatingFilterBarContext>
              <FloatingFilterBar.FilterBarObserver>
                <FilterDrawerButton
                  filterCountStyle={filterCountStyle}
                  labelStyle={labelStyle}
                />
              </FloatingFilterBar.FilterBarObserver>
              {isMobile && (
                <HeaderAttachment>
                  {hasStyleGuide && <FloatingFilterBar label={label} />}
                </HeaderAttachment>
              )}
            </FloatingFilterBarContext>
          ) : (
            <FloatingFilterButton.Provider>
              <FloatingFilterButton.InlineButtonObserver>
                <FilterDrawerButton
                  filterCountStyle={filterCountStyle}
                  labelStyle={labelStyle}
                />
              </FloatingFilterButton.InlineButtonObserver>
              {isMobile ? (
                <HeaderAttachment>
                  <FloatingFilterButton icon={null} />
                </HeaderAttachment>
              ) : null}
            </FloatingFilterButton.Provider>
          )}
        </>
      )}

      {isCarouselGrid ? (
        <CarouselHeader>
          {!isMobile ? (
            <>
              <CarouselLabel style={labelStyle}>{label}</CarouselLabel>
              <CarouselNavigationContainer>
                {hasGridCTA && (
                  <>
                    <CarouselViewAllBtn variant="border" href={gridCTA.url}>
                      {gridCTA.label ? gridCTA.label : 'View All'}
                    </CarouselViewAllBtn>
                  </>
                )}
                <VerticalSpacer />
                <CarouselNavigation />
              </CarouselNavigationContainer>
            </>
          ) : null}
        </CarouselHeader>
      ) : null}
    </BrowserHeader>
  );
};

Header.propTypes = {
  disabledFilters: PropTypes.bool,
  gender: PropTypes.string,
  /**
   * Grid CTA data for carousel CTA buttons in the header.
   */
  gridCTA: PropTypes.object,
  /**
   * Indicates whether the StyleGuide is rendered on the current grid.
   */
  hasStyleGuide: PropTypes.bool,
  /**
   * Indicates whether the page has a side bar or not, which is used to determine the position of the header text.
   */
  hideSideBar: PropTypes.bool,
  /**
   * Indicates whether the sort options should show on the current grid.
   */
  hideSortOption: PropTypes.bool,
  /**
   * Indicator whether the grid is a carousel grid.
   */
  isCarouselGrid: PropTypes.bool,
  /**
   * Indicator whether this grid is inside a tab.
   */
  isTabGrid: PropTypes.bool,
  /**
   * grid header input from builder data.
   */
  label: PropTypes.string,
  labelStyle: PropTypes.object,
  /* This config comes from the new builder CIO colection grid, it defaults to true to be backwards compatible with the current collection grid.
  If set to false, breadcrumbs will not be rendered and the placeholder will render instead */
  shouldShowCategoryNav: PropTypes.bool,
  shouldUseSharedFilters: PropTypes.bool,
};

const ProductSizeFilter = (props) => {
  const { isMobile } = useBreakpoint();
  const { primaryCategoryId } = useProductBrowser();
  const mensBottomId = config.get('public.productCategories.mensBottom');
  const mensPantId = config.get('public.productCategories.mensPant');
  const mensPantPage = primaryCategoryId === mensPantId;
  const mensBottomPage = primaryCategoryId === mensBottomId;
  const showGrid = isMobile || mensPantPage || mensBottomPage;

  return (
    <BaseProductSizeFilter
      as={isMobile ? MobileProductListingFilter : DesktopProductListingFilter}
      filterStyle={showGrid ? ProductListingFilterStyle.GRID : undefined}
      withNestedBraFilter={false}
      {...props}
    />
  );
};

const getChangedFilters = (filters, filtersToCompare) => {
  const {
    aggregationFilter: aggregationsToCompare,
    avgReviewMin: avgReviewToCompare,
  } = filtersToCompare;

  const { aggregationFilter, avgReviewMin } = filters;

  const hasNewAggregations = aggregationsToCompare
    ? Object.entries(aggregationsToCompare).some(
        ([key, value]) =>
          JSON.stringify(value) !== JSON.stringify(aggregationFilter[key])
      )
    : false;

  let newFilters = filters;

  if (hasNewAggregations) {
    newFilters = {
      ...newFilters,
      aggregationFilter: {
        ...aggregationFilter,
        ...aggregationsToCompare,
      },
    };
  }

  if (avgReviewMin !== avgReviewToCompare) {
    newFilters = {
      ...newFilters,
      avgReviewMin: avgReviewToCompare,
    };
  }

  return newFilters;
};

const ProductFilters = (props) => {
  const browserFilterState = useProductBrowserFilterState();
  const { filters } = useProductBrowser();

  // If browserFilterState is present, write our filters to the
  // browserFilterState.
  useEffect(() => {
    if (browserFilterState) {
      const setSharedFilters = browserFilterState.setFilters;
      const sharedFilters = browserFilterState.filters;

      if (!sharedFilters) {
        setSharedFilters({
          aggregationFilter: filters.aggregationFilter,
          avgReviewMin: filters.avgReviewMin,
        });
      } else {
        const newFilters = getChangedFilters(sharedFilters, filters);

        if (newFilters !== sharedFilters) {
          setSharedFilters(newFilters);
        }
      }
    }
  }, [browserFilterState, filters]);

  return <BaseProductBrowser.ProductFilters {...props} />;
};

ProductFilters.propTypes = {
  preserveFilters: PropTypes.bool,
};

const SideBar = ({
  showCategoryFields,
  shouldShowCategoryHeading,
  shouldUseSharedFilters = false,
  filterTagProps,
  ...rest
}) => {
  /*
  If the `activeCategory` object is passed into the `ProductBrowser` component
  that wraps this `Sidebar` component, it will be available in the context here.
  This object contains data pulled from the `Navigationizer` needed to render the
  category navigation matching the current category (determined by the URL path).
  The data includes details like ancestors, siblings, and descendants.
*/
  const { activeCategory } = useProductBrowser();

  return (
    <ProductBrowserSideBar {...rest}>
      {showCategoryFields && shouldShowCategoryHeading && activeCategory ? (
        <TopLevelCategoryHeading />
      ) : null}
      {showCategoryFields && activeCategory ? <ProductCategoryTree /> : null}
      <FilterTags as={BaseProductBrowser.FilterTags} />
      {!shouldUseSharedFilters && (
        <ProductFilters
          listingFilterComponent={DesktopProductListingFilter}
          sizeFilterComponent={ProductSizeFilter}
        />
      )}
    </ProductBrowserSideBar>
  );
};

SideBar.propTypes = {
  filterTagProps: PropTypes.object,
  /**
   * Used to determine whether to show the top-level category above the ProductCategoryTree.
   * `TopLevelCategoryHeading` will be hidden if `showCategoryFields` is false.
   */
  shouldShowCategoryHeading: PropTypes.bool,
  shouldUseSharedFilters: PropTypes.bool,
  /**
   * When using ProductBrowser for an FPL page, some components will not need to render.
   * This conditionally renders category-based components. eg. ProductCategoryTree,
   * BreadCrumbs, TopLevelCategoryHeading, and CategoryDropdown.
   */
  showCategoryFields: PropTypes.bool,
};

const FilterDrawer = ({ filterSettings, filterTagProps, ...rest }) => (
  <BaseFilterDrawer
    dialogStyle={filterDrawerDialogStyle}
    overlayColor={overlayColor}
    closeButtonStyle={closeButtonStyle}
    closeIcon={<CloseIcon />}
    {...rest}
  >
    <BaseFilterDrawer.Content>
      <FilterTags
        as={BaseFilterDrawer.FilterTags}
        wrapperStyle={filterTagWrapperStyle}
        noFiltersAppliedStyle={noFiltersAppliedStyle}
        {...filterTagProps}
      />
      <BaseFilterDrawer.Filters>
        <BaseProductBrowser.ProductFilters
          filterSettings={filterSettings}
          listingFilterComponent={MobileProductListingFilter}
          sizeFilterComponent={ProductSizeFilter}
        />
      </BaseFilterDrawer.Filters>
      <BaseFilterDrawer.ApplyFilters buttonStyle={applyFiltersButtonStyle} />
    </BaseFilterDrawer.Content>
  </BaseFilterDrawer>
);

FilterDrawer.propTypes = {
  filterSettings: PropTypes.arrayOf(PropTypes.object),
  filterTagProps: PropTypes.object,
};

function ProductGrid({
  breakpoints,
  fplId,
  gridSlotsAsset,
  imageType,
  index,
  initialProducts,
  initialSortOption,
  isCarouselGrid = false,
  showLastChance = false,
  maxItemCount,
  preserveFilters = false,
  pdpLinkOverride,
  productSource,
  showResultsLength = false,
  showColorsLink = false,
  showGridReviews = false,
  isGqlProducts = false,
  isCioSearch = false,
  isCioBrowse = false,
  isSaleGrid = false,
  cioFilterName,
  cioFilterValue,
}) {
  const {
    filters: inputFilters,
    setFilterDrawerOpen,
    sort,
    setTotalCount,
  } = useProductBrowser();
  const { isMobile } = useBreakpoint();
  const browserFilterState = useProductBrowserFilterState();
  const actions = useProductActions();
  const apolloClient = useApolloClient();
  const { breakpoint } = useGridState();
  const isFirstRender = useRef(true);
  const initialBoostedFplId = useRef(null);
  const productImageType = useProductImageType();
  const { isLoading, startLoading } = useLoadingStatus();
  const region = useRegion();
  const { gender } = useGender();

  const { debugData: { algorithmTrace } = {} } = initialProducts;
  const { query } = useRouter();
  const cioClient = useCioClient();

  const { isVip, isElite } = useMembership();
  const isSecondarySite = useSecondarySite();
  const domain = useDomain();
  const { tld } = domain;
  const { isBuilderGridGuideEnabled } = useAdminTools();

  useReportAlgorithmTrace(algorithmTrace);

  const {
    query: { mpid },
  } = useRouter();

  const gridSlotsAssets = Array.isArray(gridSlotsAsset)
    ? gridSlotsAsset.map((asset) =>
        isMobile && asset.mobile_container?.length
          ? asset.mobile_container
          : asset.container
      )
    : [gridSlotsAsset];

  const gridAssetContainers = useAssets(gridSlotsAssets, {
    multi: true,
  });
  const { enabled: retailDiscounting } = useHasFeature(
    featureNames.RETAILPRICETEST
  );
  const autoAppliedFiltersDisabled = config.get(
    'public.brand.autoAppliedFiltersDisabled'
  );

  const filters = useDefaultProductFilters(inputFilters);

  const currentSort = sort ? sort?.sort : undefined;
  const currentFilters = filters.aggregationFilter;
  const filterKeys = Object.keys(currentFilters);
  const category = filters.categoryIds.length
    ? filters.categoryIds
    : filters.backgroundCategoryIds;

  const [, setCookie, removeCookie] = useCookies(Object.keys(cookieSizeMap));
  const gridContext = useGridScrollContext();
  const handleSetScroll = gridContext?.handleSetScroll || null;

  // pdp page preselects size based on current selected filter stored as a cookie.
  const sizingCookies = useMemo(() => {
    return getSizeCookies(filters.aggregationFilter);
  }, [filters.aggregationFilter]);
  const sizingCookiesRef = useRef(sizingCookies);

  useEffect(() => {
    // remove all sizing cookies if autofilter is disabled
    if (autoAppliedFiltersDisabled) {
      for (const cookieName of Object.keys(sizingCookies)) {
        removeCookie(cookieName);
      }
    }
  }, [autoAppliedFiltersDisabled, removeCookie, sizingCookies]);

  useEffect(() => {
    for (const [cookieName, cookieSize] of Object.entries(sizingCookies)) {
      if (cookieSize) {
        setCookie(cookieName, `"${cookieSize}"`);
      } else if (!cookieSize && sizingCookiesRef.current[cookieName]) {
        // This will remove cookies for values that were removed on the grid and prevents cookies from being removed
        // that were added from other sources (e.g. CF pages)
        removeCookie(cookieName);
      }
    }
    sizingCookiesRef.current = sizingCookies;
  }, [sizingCookies, setCookie, removeCookie]);

  let boostedFplId = filters.boostedFplId;
  if (initialSortOption?.sort !== sort?.sort) {
    initialBoostedFplId.current = filters.boostedFplId;
    boostedFplId = null;
  } else if (
    initialSortOption?.sort === sort?.sort &&
    initialBoostedFplId.current
  ) {
    boostedFplId = initialBoostedFplId.current;
  }

  const payload = useMemo(() => {
    const ignoreBoostedFpl =
      sort?.sort === 'fplasc' && sort?.sort === initialSortOption.sort;

    modifyFiltersForLastChance({
      filters,
      alwaysShowLastChance: showLastChance,
    });

    const payloadValue = {
      ...filters,
      sort: sort ? sort?.sort : undefined,
      boostedIds: mpid ? [parseInt(mpid)] : [],
    };

    // only apply if it's true to avoid cache miss
    if (ignoreBoostedFpl) {
      payloadValue.ignoreBoostedFpl = ignoreBoostedFpl;
    } else {
      payloadValue.boostedFplId = initialBoostedFplId.current
        ? boostedFplId
        : filters.boostedFplId;
    }
    return payloadValue;
  }, [showLastChance, boostedFplId, filters, sort, initialSortOption, mpid]);

  const payloadKey = useMemo(() => JSON.stringify(payload), [payload]);
  const [fplCode] = filters.fplCodes ?? [];

  const [state, dispatch] = useProductListingsReducer({
    key: payloadKey,
    initialProducts,
  });

  const { productPricingMap } = useInitialProductPricing({
    dispatch,
    key: payloadKey,
    initialProducts,
    useDelayedPricing: isGqlProducts || isCioSearch || isCioBrowse,
    isVip,
  });

  const { enabled: shouldUseImageService } = useHasFeature(
    featureNames.SHOULD_USE_IMAGE_SERVICE_URL
  );
  const { enabled: removeOriginFlag } = useHasFeature(
    featureNames.REMOVE_ORIGIN
  );
  const { enabled: surfaceHiddenFacets } = useHasFeature(
    featureNames.SURFACE_HIDDEN_FACETS
  );

  const {
    productRelatedProductsMap,
    isLoading: isRelatedProductSwatchesLoading,
  } = useInitialProductRelatedProducts({
    initialProducts,
    useDelayedRelatedProducts: isGqlProducts || isCioSearch || isCioBrowse,
    shouldUseImageService:
      shouldUseImageService && (isCioSearch || isCioBrowse),
  });

  const filteredProductRelatedProductsMap =
    isCioSearch || isCioBrowse
      ? filterFutureProducts({
          products: productRelatedProductsMap,
        })
      : productRelatedProductsMap;

  // TODO: find performant way to query correct related product pricing.
  // https://techstyle-prod.atlassian.net/browse/FLD-1769
  const fallbackRelatedProductPriceMap = {};
  if (isCioSearch || isCioBrowse) {
    Object.entries(filteredProductRelatedProductsMap).forEach(
      ([key, value]) => {
        if (!value.relatedProductIdObjectList) {
          return;
        }
        value.relatedProductIdObjectList.forEach((relatedProduct) => {
          fallbackRelatedProductPriceMap[relatedProduct?.masterProductId] =
            productPricingMap[key];
        });
      }
    );
  }

  const { pages, totalCount, aggregations } = state;
  const isStale = payloadKey !== state.payloadKey;

  // purposefully excluding handleSetScroll function to avoid triggering the scroll on inital page load
  useEffect(() => {
    if (!browserFilterState) {
      if (isFirstRender.current) {
        isFirstRender.current = false;
      } else if (!isStale && handleSetScroll) {
        handleSetScroll();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStale, preserveFilters, browserFilterState]);

  const { aggregationFilter, ...currentBaseFilter } = payload;
  const currentBaseFilterKey = useMemo(
    () => JSON.stringify(currentBaseFilter),
    [currentBaseFilter]
  );
  const [baseFilterKey, setBaseFilterKey] = useState(currentBaseFilterKey);
  const { setFilterSettings } = useProductBrowser();
  const { enabled: isPricingServiceFeatureEnabled } = useHasFeature(
    featureNames.USE_PRICING_SERVICE
  );
  const shouldUsePricingService =
    isPricingServiceFeatureEnabled || isSecondarySite;

  useEffect(() => {
    if (
      currentBaseFilterKey === baseFilterKey ||
      isStale ||
      !aggregations ||
      isCioSearch ||
      isCioBrowse
    ) {
      return;
    }

    const filterSettings = getFilterSettings({
      aggregations,
      region,
      tld,
      gender,
      domain,
    });

    setFilterSettings(filterSettings);
    setBaseFilterKey(currentBaseFilterKey);
  }, [
    aggregations,
    baseFilterKey,
    currentBaseFilterKey,
    isStale,
    setFilterSettings,
    region,
    tld,
    gender,
    isCioSearch,
    isCioBrowse,
    domain,
  ]);

  useEffect(() => {
    setTotalCount(totalCount);
  }, [setTotalCount, totalCount]);

  const slots = useMemo(() => {
    if (!gridAssetContainers || gridAssetContainers.length < 1) {
      return [];
    }

    const mappedContainers = gridAssetContainers
      .filter(
        (gridAssetContainer) =>
          gridAssetContainer &&
          gridAssetContainer.data &&
          gridAssetContainer.data.assets
      )
      .map((gridAssetContainer) => {
        const slotAsset = Array.isArray(gridSlotsAsset)
          ? gridSlotsAsset.find((gridSlotAsset) => {
              if (isMobile && gridSlotAsset.mobile_container) {
                return (
                  gridSlotAsset.mobile_container === gridAssetContainer.name
                );
              }

              return gridSlotAsset.container === gridAssetContainer.name;
            })
          : null;
        if (slotAsset && slotAsset.position) {
          gridAssetContainer.position = slotAsset.position;
        }
        if (slotAsset && slotAsset.row) {
          gridAssetContainer.row = slotAsset.row;
        }
        if (slotAsset && slotAsset.column) {
          gridAssetContainer.column = slotAsset.column;
        }
        if (slotAsset && slotAsset.size) {
          gridAssetContainer.size = slotAsset.size;
        }

        return gridAssetContainer.data.assets.map((asset) => {
          const { customVars } = asset.options;

          return {
            asset,
            itemType: 'asset',
            position: gridAssetContainer.position
              ? gridAssetContainer.position
              : customVars.position,
            row: gridAssetContainer.row
              ? gridAssetContainer.row
              : customVars.row,
            column: gridAssetContainer.column
              ? gridAssetContainer.column
              : customVars.column,
            cellsWide:
              gridAssetContainer.size ||
              customVars.cellsWide ||
              customVars.size,
            cellsTall: customVars.cellsTall,
            minColumns: customVars.minColumns,
            maxColumns: customVars.maxColumns,
            videoAspectRatio: customVars.videoAspectRatio,
          };
        });
      });
    return [].concat(...mappedContainers);
  }, [gridAssetContainers, gridSlotsAsset, isMobile]);

  const loadItems = useCallback(
    async ({ startIndex, stopIndex }) => {
      /* Multiple re-rendering of the ProductBrowser cause lead to multiple calls to the loadItems function,
       the solution is return early if the grid is still loading.
       This removes the dupe call for most cases, but it's still present in Clear all filters click. */
      if (isLoading) {
        return;
      }
      const stopLoading = startLoading();
      const startPage = Math.floor(startIndex / pageSize) + 1;
      const stopPage = Math.floor((stopIndex - 1) / pageSize) + 1;

      for (let page = startPage; page <= stopPage; page++) {
        const hasPage = state.payloadKey === payloadKey && pages[page] != null;
        if (!hasPage) {
          let payloads = null;

          if (isCioBrowse) {
            payloads = await getCioBrowseProducts({
              page,
              pageSize,
              payload,
              cioFilterName,
              cioFilterValue,
              cioClient,
              apolloClient,
              isElite,
              isVip,
              shouldShowWarehouseProducts: isSecondarySite,
              shouldHideOriginRestricted: removeOriginFlag,
              shouldUsePricingService,
              shouldUseImageService,
              isSaleGrid,
              domain,
              surfaceHiddenFacets,
            });
          } else if (isCioSearch) {
            payloads = await getCioSearchProducts({
              page,
              pageSize,
              payload,
              searchQuery: query.q,
              cioClient,
              apolloClient,
              isElite,
              isVip,
              shouldShowWarehouseProducts: isSecondarySite,
              shouldHideOriginRestricted: removeOriginFlag,
              shouldUsePricingService,
              shouldUseImageService,
              domain,
            });
          } else if (isGqlProducts) {
            payloads = await getGqlProducts({
              apolloClient,
              page,
              pageSize,
              payload,
              retailDiscounting,
            });
          } else {
            payloads = await getV2Products({
              page,
              pageSize,
              payload,
              retailDiscounting,
              actions,
              productSource,
            });
          }

          try {
            dispatch({
              type: 'LOAD_PRODUCTS',
              payload: payloads,
              payloadKey,
            });
            if (isCioSearch || isCioBrowse) {
              setFilterSettings(payloads[0].aggregations);
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
          } finally {
            stopLoading();
          }
        }
      }
    },
    [
      isLoading,
      startLoading,
      state.payloadKey,
      payloadKey,
      pages,
      isCioBrowse,
      isCioSearch,
      isGqlProducts,
      payload,
      cioFilterName,
      cioFilterValue,
      cioClient,
      apolloClient,
      isElite,
      isVip,
      isSecondarySite,
      removeOriginFlag,
      shouldUsePricingService,
      shouldUseImageService,
      isSaleGrid,
      domain,
      surfaceHiddenFacets,
      query.q,
      retailDiscounting,
      actions,
      productSource,
      dispatch,
      setFilterSettings,
    ]
  );

  const renderItems = useCallback(
    ({ isVisible, items, startIndex, stopIndex, lastKnownHeight }) => {
      return (
        <BaseProductGrid
          boostedFplId={boostedFplId}
          breakpoints={breakpoints}
          fplCode={fplCode}
          fplId={fplId}
          imageAspectRatio={imageAspectRatio}
          items={isVisible ? items : null}
          isCarouselGrid={isCarouselGrid}
          payloadKey={payloadKey}
          placeholderProps={{
            itemCount: stopIndex - startIndex,
            totalHeight: lastKnownHeight,
          }}
          productLinkOverride={pdpLinkOverride}
          productImageType={productImageType}
          productSource={productSource}
          startIndex={startIndex}
          showColorsLink={showColorsLink}
          showGridReviews={showGridReviews}
          isBuilderGridGuideEnabled={isBuilderGridGuideEnabled}
        />
      );
    },
    [
      boostedFplId,
      breakpoints,
      fplCode,
      fplId,
      isCarouselGrid,
      payloadKey,
      pdpLinkOverride,
      productImageType,
      productSource,
      showColorsLink,
      showGridReviews,
      isBuilderGridGuideEnabled,
    ]
  );

  const getItems = useCallback(
    ({ itemStartIndex, itemStopIndex, startIndex, stopIndex }) => {
      // TODO? Some sort of throttle applied to loadItems retries
      if (state.payloadKey !== payloadKey) {
        return null;
      }

      const startPage = Math.floor(itemStartIndex / pageSize) + 1;
      let startOffset = itemStartIndex % pageSize;
      const stopPage = Math.floor((itemStopIndex - 1) / pageSize) + 1;
      const items = [];
      for (let page = startPage; page <= stopPage; page++) {
        const pageItems = pages[page];
        if (pageItems) {
          const batchItems = startOffset
            ? pageItems.slice(startOffset)
            : pageItems;

          items.push(...batchItems);
        } else {
          // Don't have all the pages yet, bail out!
          return null;
        }
        startOffset = 0;
      }

      const length = itemStopIndex - itemStartIndex;

      // ! Careful - this can trigger an infinite loop (via loadItems) depending on
      // ! the truthiness evaluation. This should fail more gracefully.
      if (items.length < length) {
        return null; // Don't have expected number of items, return nothing!
      }
      if (items.length > length) {
        items.length = length; // Truncate.
      }

      return items;
    },
    [pages, payloadKey, state.payloadKey]
  );

  let itemCount;

  if (totalCount === 0 && isStale) {
    itemCount = null;
  } else {
    itemCount = maxItemCount ? Math.min(maxItemCount, totalCount) : totalCount;
  }

  const renderInGridAssets =
    !isMobile || (isMobile && (showMobileInGridAssets || index !== 0));

  return (
    <ProductGridPricingContext
      productGridPricing={{
        ...fallbackRelatedProductPriceMap,
        ...productPricingMap,
      }}
    >
      {showResultsLength && (
        <ResultsCountWrapper>
          {itemCount !== null && !isLoading && (
            <DynamicFormattedMessage
              id="search.results_heading_total_count"
              defaultMessage="{count} results"
              values={{
                count: itemCount,
              }}
            />
          )}
        </ResultsCountWrapper>
      )}
      {totalCount === 0 && !isStale ? (
        <NoProductsMessage onClickButton={() => setFilterDrawerOpen(true)} />
      ) : (
        <ProductGridRelatedProductsContext
          productGridRelatedProducts={filteredProductRelatedProductsMap}
          isLoading={isRelatedProductSwatchesLoading}
        >
          <VirtualScroll
            getItems={getItems}
            itemCount={itemCount}
            loadItems={loadItems}
            renderItems={renderItems}
            slots={renderInGridAssets ? slots : EMPTY_ARRAY}
            stepSize={breakpoint.columns}
            targetBatchSize={pageSize}
          />
        </ProductGridRelatedProductsContext>
      )}
      <GridFilterSegmentEvents
        filterKeys={filterKeys}
        currentFilters={currentFilters}
        currentSort={currentSort}
        category={category}
        list={productSource}
      />
    </ProductGridPricingContext>
  );
}

ProductGrid.propTypes = {
  breakpoints: BreakpointsShape,
  /* In CIO, the filter name and filter value help distinguish a specific segment of initial products to load.
   For example, `cioFilterName` can be either group ID or collection ID, while `cioFilterValue` might
   correspond to the headliner collection or the men's shorts category.
  Note: These concepts should not be confused with "filtering" results based on attributes like color or size. */
  cioFilterName: PropTypes.string,
  cioFilterValue: PropTypes.string,
  fplId: PropTypes.number,
  gridSlotsAsset: PropTypes.any,
  imageType: PropTypes.string,
  index: PropTypes.number,
  initialProducts: PropTypes.object,
  initialSortOption: individualSortOptionPropType,
  isCarouselGrid: PropTypes.bool,
  isCioBrowse: PropTypes.bool,
  isCioSearch: PropTypes.bool,
  isGqlProducts: PropTypes.bool,
  isSaleGrid: PropTypes.bool,
  /**
   * Number of products to render with VirtualScroll component.
   */
  maxItemCount: PropTypes.number,
  pdpLinkOverride: PropTypes.string,
  preserveFilters: PropTypes.bool,
  productSource: PropTypes.string,
  showColorsLink: PropTypes.bool,
  showGridReviews: PropTypes.bool,
  showLastChance: PropTypes.bool,
  showResultsLength: PropTypes.bool,
};

function MobileNavigation() {
  const gridContext = useGridScrollContext();
  const mobileRef = gridContext?.mobileRef || null;
  return (
    <BaseProductBrowser.MobileNavigation ref={mobileRef}>
      <CategoryDropdown />
    </BaseProductBrowser.MobileNavigation>
  );
}

function Body({ gridColumnsSettings, children, className }) {
  const breakpoints = addNewGridColumnSettings(gridColumnsSettings);
  return (
    <BaseProductBrowser.Body className={className}>
      <DropdownOverlay />
      <ProductListViewedProvider>
        <GridProvider
          breakpoints={breakpoints}
          defaultBreakpoint={breakpoints[0]}
        >
          {children}
        </GridProvider>
      </ProductListViewedProvider>
    </BaseProductBrowser.Body>
  );
}

Body.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  gridColumnsSettings: gridColumnsSettingsType,
};

function SharedFilterWatcher() {
  const browserFilterState = useProductBrowserFilterState();

  const { filters, setFilters, setSort, sort } = useProductBrowser();

  const sharedFilters = browserFilterState?.filters;
  const sharedSort = browserFilterState?.sort;

  useEffect(() => {
    if (sharedFilters) {
      const newFilters = getChangedFilters(filters, sharedFilters);

      if (newFilters !== filters) {
        setFilters(newFilters);
      }
    }
  }, [sharedFilters, filters, setFilters]);

  useEffect(() => {
    if (sharedSort && sort !== sharedSort) {
      setSort(sharedSort);
    }
  }, [sharedSort, setSort, sort]);

  return null;
}

function ProductBrowser({
  activeCategory,
  aggregations = [],
  baseFilters,
  breadcrumbs,
  children,
  enableQueryParams,
  filterSettings: inputFilterSettings = [],
  gridSlotsAsset,
  initialFilters: initialFiltersFromProps = {},
  initialSortOption,
  shouldUseSharedFilters,
  profileSizes,
  sortOptions,
  ...rest
}) {
  const { tld } = useDomain();
  const region = useRegion();
  const { gender } = useGender();
  const domain = useDomain();
  const colorSwatches = useMemo(() => getColorSwatches(), []);

  let filterSettings = [];
  if (!inputFilterSettings?.length && aggregations?.length) {
    filterSettings = getFilterSettings({
      aggregations,
      region,
      tld,
      gender,
      domain,
    });
  } else {
    filterSettings = inputFilterSettings;
  }

  return (
    <BaseProductBrowser
      activeCategory={activeCategory}
      baseFilters={baseFilters}
      breadcrumbs={breadcrumbs}
      colorSwatches={colorSwatches}
      filterSettings={filterSettings}
      initialFilters={initialFiltersFromProps}
      initialSortOption={initialSortOption}
      profileSize={profileSizes}
      sortOptions={sortOptions}
      enableQueryParams={enableQueryParams}
      {...rest}
    >
      {shouldUseSharedFilters && <SharedFilterWatcher />}
      <CategoryLabelProvider useLabels={useCategoryLabels}>
        <OptionLabelProvider useLabels={useOptionLabels}>
          {children}
        </OptionLabelProvider>
      </CategoryLabelProvider>
    </BaseProductBrowser>
  );
}

ProductBrowser.propTypes = {
  /**
   * Custom category hierarchy to use instead of category-based breadcrumbs
   * from Redux store
   */
  activeCategory: PropTypes.object,
  /**
   * Array of aggregations to power the filterSettings.
   * Can pass in this or filterSettings.
   */
  aggregations: PropTypes.array,
  /**
   * Temporarily passing in empty object to fix bug.
   */
  autoAppliedFilters: PropTypes.object,
  /**
   * The filter settings that cannot be unapplied; if the user resets/clears all
   * filters, this is what they will reset to.
   */
  baseFilters: PropTypes.object,
  /**
   * Custom breadcrumbs hierarchy grabbed from navigationizer.
   */
  breadcrumbs: PropTypes.array,
  children: PropTypes.node,
  /**
   * If true, updates query parameters every time filters are updated.
   */
  enableQueryParams: PropTypes.bool,
  filterSettings: PropTypes.arrayOf(PropTypes.object),
  /**
   * Grid CTA data from builder
   */
  gridCTA: PropTypes.object,
  /**
   * During getInitialProps routeInfo from ctx includes the inheritedRouteParamsJson
   * and routeParamsJson. This is the dev-defined json from rewrites, gridSlotsAsset
   * comes from these routeParams.
   */
  gridSlotsAsset: PropTypes.string,
  /**
   * The initial filter settings. It's possible for this to be different from
   * `baseFilters`, because other filter settings can be initialized. For instance,
   * profile applied filtering and/or url parameters.
   */
  initialFilters: PropTypes.object,
  /**
   * Initial sort to be used.
   */
  initialSortOption: individualSortOptionPropType,
  /**
   * RB key and defaultMessage based on categoryHierarchy. Should be updated to use LabelProvider
   */
  label: PropTypes.object,
  /**
   * For data analytics, primaryFplId is sent to ProductGrid. Can come from product.json
   * in rewrites. Either baseFilters.backgroundFplIds or baseFilter.fplIds
   */
  primaryFplId: PropTypes.number,
  /**
   * Provides insight for our Data Analytics Team into where a customer discovered
   *  a product. It is determined by where this <ProductGrid /> component will be
   * used. For more information about product source specifications please
   * visit: https://confluence.techstyle.net/display/EDM/Segment+-+P+Source+Specifications
   */
  productSource: PropTypes.string,
  /**
   * A user's saved sizes from their profile.
   */
  profileSizes: PropTypes.object,
  /**
   * We only want to show CategoryDropdown if there are subcategories or siblings. CategoryDropdown
   * will be  hidden if `showCategoryFields` is false.
   */
  shouldShowCategoryDropdown: PropTypes.bool,
  /**
   * Used to determine whether to show the top-level category above the ProductCategoryTree.
   * `TopLevelCategoryHeading` will be hidden if `showCategoryFields` is false.
   */
  shouldShowCategoryHeading: PropTypes.bool,
  /**
   * Only has an effect if the component is wrapped by a ProductBrowserFilterContext. When false,
   * ignores changes to the ProductBrowserFilterContext. When true, consumes the value of that context
   * and uses its stored filters.
   * */
  shouldUseSharedFilters: PropTypes.bool,
  /**
   * When using ProductBrowser for an FPL page, some components will not need to render.
   * This conditionally renders category-based components. eg. ProductCategoryTree,
   * BreadCrumbs, TopLevelCategoryHeading, and CategoryDropdown.
   */
  showCategoryFields: PropTypes.bool,
  /**
   * All sort options to choose from.
   */
  sortOptions: PropTypes.arrayOf(individualSortOptionPropType),
};

export default ProductBrowser;
ProductBrowser.Body = Body;
ProductBrowser.DesktopProductListingFilter = DesktopProductListingFilter;
ProductBrowser.DropdownOverlay = DropdownOverlay;
ProductBrowser.FilterDrawer = FilterDrawer;
ProductBrowser.GridContent = GridContent;
ProductBrowser.Header = Header;
ProductBrowser.MobileNavigation = MobileNavigation;
ProductBrowser.MobileProductListingFilter = MobileProductListingFilter;
ProductBrowser.LoadingGridsSpinner = LoadingGridsSpinner;
ProductBrowser.ProductFilters = ProductFilters;
ProductBrowser.ProductGrid = ProductGrid;
ProductBrowser.ProductSizeFilter = ProductSizeFilter;
ProductBrowser.SideBar = SideBar;
ProductBrowser.TopLevelCategoryHeading = TopLevelCategoryHeading;
